import { matchesWorkflowSearch } from '@/components/projects/workflows/shared/workflowSearch'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useChains } from '@/hooks'
import { cn } from '@/lib/utils'
import type { ChainDefinitionFilters, ChainDefinitionSummary, LinkedEntityScope } from '@/types'
import { Check, Loader2, Play, Search } from 'lucide-react'
import { useMemo, useState } from 'react'

export type TriggerableChain = Pick<ChainDefinitionSummary, 'id' | 'displayName' | 'yamlContent'>

interface TriggerChainFromEntityDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (chain: TriggerableChain) => void
  linkedEntityType: LinkedEntityScope
}

const CHAINS_PAGINATION = { maxResultCount: 100, skipCount: 0 }

export function TriggerChainFromEntityDialog({
  projectId,
  open,
  onOpenChange,
  onSelect,
  linkedEntityType,
}: TriggerChainFromEntityDialogProps) {
  const [selectedChain, setSelectedChain] = useState<TriggerableChain | null>(null)
  const [search, setSearch] = useState('')

  const filters = useMemo<ChainDefinitionFilters>(
    () => ({ status: 'active', search: '', type: 'all', category: 'all', linkedEntityScope: linkedEntityType }),
    [linkedEntityType],
  )
  const { data, isLoading } = useChains(projectId, CHAINS_PAGINATION, filters)
  const chains = data?.items ?? []

  const filtered = chains.filter((c) => matchesWorkflowSearch(c, search))

  const handleClose = () => {
    setSelectedChain(null)
    setSearch('')
    onOpenChange(false)
  }

  const handleNext = () => {
    if (!selectedChain) return
    const chain = selectedChain
    setSelectedChain(null)
    setSearch('')
    onOpenChange(false)
    onSelect(chain)
  }

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Select Chain</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Select a chain to start.
          </p>

          <div className="flex max-h-[440px] flex-col">
            {isLoading ? (
              <div
                className="flex items-center justify-center py-16"
                role="status"
                aria-live="polite"
                aria-label="Loading chains"
              >
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
              </div>
            ) : chains.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="w-full rounded-lg border border-dashed p-6 text-center"
                  data-testid="no-chains-empty-state"
                >
                  <p className="text-sm text-muted-foreground">No chains available.</p>
                </div>
              </div>
            ) : (
              <div className="flex h-[440px] min-h-0 flex-col gap-4">
                <div className="relative shrink-0">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input
                    placeholder="Search by name or description"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value)
                      setSelectedChain(null)
                    }}
                    className="pl-9"
                    aria-label="Search chains"
                    data-testid="chain-search-input"
                  />
                </div>

                {filtered.length === 0 ? (
                  <div
                    className="flex flex-1 items-center justify-center rounded-lg border border-dashed p-6 text-center"
                    data-testid="no-chain-results-empty-state"
                  >
                    <p className="text-sm text-muted-foreground">No chains match your search.</p>
                  </div>
                ) : (
                  <TooltipProvider>
                    <div
                      className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1"
                      role="listbox"
                      aria-label="Available chains"
                    >
                      {filtered.map((chain) => {
                        const isSelected = selectedChain?.id === chain.id
                        return (
                          <button
                            key={chain.id}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => setSelectedChain({ id: chain.id, displayName: chain.displayName, yamlContent: chain.yamlContent })}
                            className={cn(
                              'w-full rounded-lg border p-3 text-left transition-colors flex items-center justify-between gap-2',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-border hover:border-primary/50 hover:bg-muted/50',
                            )}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="truncate text-sm font-medium">{chain.displayName}</p>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">{chain.displayName}</TooltipContent>
                                </Tooltip>
                                <Badge
                                  variant="outline"
                                  className="shrink-0 text-xs"
                                  style={{ borderColor: 'rgba(118,124,150,1)' }}
                                >
                                  {chain.isSystem ? 'System' : 'Custom'}
                                </Badge>
                              </div>
                              {chain.description && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                                      {chain.description}
                                    </p>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">{chain.description}</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            {isSelected && (
                              <Check className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                            )}
                          </button>
                        )
                      })}
                    </div>
                  </TooltipProvider>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t pt-2">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              disabled={!selectedChain}
              data-testid="next-to-chain-inputs-button"
            >
              <Play className="h-4 w-4" />
              Continue Start
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
